import type { Theme } from "@mui/material/styles";

/**
 * Shell top-bar tokens from the approved Slate + Emerald palette
 * (docs/design-concepts/slate-emerald-palette.md, "sidebar" roles). The bar is
 * always dark in both color schemes, so these do not follow the page theme.
 */
export const SHELL_BAR = {
  /** slate-900: bar background (palette `--sidebar`). */
  background: "#0F172A",
  /** slate-800: raised control on the bar (mode pill, palette `--sidebar-accent`). */
  raised: "#1E293B",
  /** slate-300: icons and resting text (palette `--sidebar-foreground`). */
  foreground: "#CBD5E1",
  /** slate-400: inactive mode tabs and placeholders. */
  muted: "#94A3B8",
  /** slate-100: active mode tab and strong text. */
  strong: "#FFFFFF",
  /** emerald-500: active mode underline (palette `--sidebar-primary`). */
  accent: "#10B981",
  /** emerald-400: mode pill icon. */
  accentSoft: "#34D399",
  /** Hairlines and the search field on the bar (palette `--sidebar-border`). */
  border: "rgba(255,255,255,0.12)",
  field: "rgba(255,255,255,0.07)",
  hover: "rgba(255,255,255,0.08)",
  /** slate-700 / slate-200: initials avatar on the bar (mockup `.topb .av.ini`). */
  avatarFallback: "#334155",
  avatarFallbackText: "#E2E8F0",
  /** Platform's emerald initials avatar (mockup platform `.av.e`). */
  avatarEmerald: "#D1FAE5",
  avatarEmeraldText: "#065F46",
  /** Platform's search trigger (mockup `.tb-search`). */
  fieldRaised: "#1E293B",
  fieldRaisedBorder: "#273449",
} as const;

/**
 * Light shell surfaces from the mockups (sub-menu, search dialog, user menu,
 * phone switcher). Slate scale from the approved palette.
 */
export const SHELL_SURFACE = {
  /** slate-900: active sub-menu text. */
  ink: "#0F172A",
  /** slate-700: menu item text. */
  ink2: "#334155",
  /** slate-600: inactive sub-menu text. */
  ink3: "#475569",
  /** slate-500: secondary text and menu icons. */
  muted: "#64748B",
  /** slate-400: inactive sub-menu icons and search breadcrumbs. */
  muted2: "#94A3B8",
  /** slate-200: hairlines. */
  line: "#E2E8F0",
  /** Lighter hairline between phone switcher rows. */
  line2: "#EEF2F6",
  /** slate-100: selected search row and segmented-control track. */
  fill: "#F1F5F9",
  /** emerald-700: active phone switcher title. */
  activeTitle: "#047857",
  /** emerald-50: active row tint in the platform phone switcher. */
  activeTint: "#ECFDF5",
  /** emerald-500: search input focus indicator. */
  focus: "#10B981",
  surface: "#FFFFFF",
  segmentShadow: "0 1px 2px rgba(15,23,42,.12)",
  popShadow: "0 20px 50px rgba(15,23,42,.22), 0 2px 6px rgba(15,23,42,.08)",
  sheetShadow: "0 -10px 30px rgba(15,23,42,.18)",
  dropdownShadow: "0 18px 40px -10px rgba(15,23,42,.35), 0 0 0 1px rgba(15,23,42,.08)",
  sheetHandle: "#CBD5E1",
} as const;

/** Modal scrims: business is lighter than platform (mockup `.scrim`). */
export const SHELL_BACKDROP = {
  business: "rgba(15,23,42,.28)",
  platform: "rgba(15,23,42,.38)",
} as const;

/** Which staff app's variant of the shared chrome to draw. */
export type ShellAppearance = keyof typeof SHELL_BACKDROP;

/**
 * Brand accent for the shell's light surfaces (page sub-menu, section tabs,
 * phone switcher): the palette `primary` role, emerald-600 on light and
 * emerald-500 on dark. The shell uses it instead of the app theme's
 * `primary.main`, which is not emerald in every staff app (platform is blue).
 */
export const SHELL_ACCENT = {
  light: "#059669",
  dark: SHELL_BAR.accent,
} as const;

type AccentProperty = "color" | "backgroundColor" | "outlineColor";

/** `property: SHELL_ACCENT` for the active color scheme, as an sx fragment. */
export function shellAccent(
  theme: Theme,
  property: AccentProperty,
): Record<string, unknown> {
  return {
    [property]: SHELL_ACCENT.light,
    ...theme.applyStyles("dark", { [property]: SHELL_ACCENT.dark }),
  };
}

type SurfaceRole = Exclude<
  keyof typeof SHELL_SURFACE,
  "segmentShadow" | "popShadow" | "sheetShadow" | "dropdownShadow" | "sheetHandle"
>;
type ShellColorRole = SurfaceRole | "accent";

/** Dark-scheme stand-ins: the light mockup values follow the theme in dark mode. */
function darkColor(theme: Theme, role: ShellColorRole): string {
  const palette = (theme.vars ?? theme).palette;
  switch (role) {
    case "ink":
    case "ink2":
      return palette.text.primary;
    case "ink3":
    case "muted":
      return palette.text.secondary;
    case "muted2":
      return palette.text.disabled;
    case "line":
    case "line2":
      return palette.divider;
    case "fill":
      return palette.action.hover;
    case "activeTint":
      return palette.action.selected;
    case "surface":
      return palette.background.paper;
    case "accent":
    case "activeTitle":
    case "focus":
      return SHELL_ACCENT.dark;
  }
}

/**
 * Light-surface colors as one sx fragment: the mockup's slate values in the
 * light scheme, theme roles in the dark scheme. Pass every color of one style
 * object in a single call so the dark-scheme selectors do not overwrite each
 * other.
 */
export function shellColors(
  theme: Theme,
  styles: Partial<Record<string, ShellColorRole>>,
): Record<string, unknown> {
  const light: Record<string, string> = {};
  const dark: Record<string, string> = {};
  for (const [property, role] of Object.entries(styles)) {
    if (!role) continue;
    light[property] = role === "accent" ? SHELL_ACCENT.light : SHELL_SURFACE[role];
    dark[property] = darkColor(theme, role);
  }
  return { ...light, ...theme.applyStyles("dark", dark) };
}
